/**
 * @fileoverview Hook de Actions Layer para gestión de grabación de audio en el Inbox.
 * Permite iniciar, detener y cancelar grabaciones, gestionando el tiempo, tamaño máximo y callbacks de finalización.
 *
 * @module useAudioRecorder
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getSupportedRecordingMimeType, isSafari } from "../../../util/audioUtils";

const MAX_AUDIO_SIZE = 10 * 1024 * 1024;
const RECORDING_TIMESLICE_MS = 1000;
const SAFARI_STOP_DELAY_MS = 150;

export const useAudioRecorder = ({ onFinish }) => {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const stopTimeoutRef = useRef(null);
  const chunksRef = useRef([]);
  const cancelledRef = useRef(false);

  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearStopTimeout = useCallback(() => {
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
  }, []);

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setRecording(false);
    setSeconds(0);
    chunksRef.current = [];
    cancelledRef.current = false;
  }, []);

  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setSeconds(prev => prev + 1);
    }, 1000);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("MediaDevices not supported");
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      streamRef.current = stream;

      const mimeType = getSupportedRecordingMimeType();
      const options = mimeType ? { mimeType } : undefined;

      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      chunksRef.current = [];
      cancelledRef.current = false;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        clearTimer();
        clearStopTimeout();

        if (cancelledRef.current) {
          stopTracks();
          reset();
          return;
        }

        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || mimeType || "audio/mp4"
        });

        if (!blob.size) {
          console.error("Empty audio recording — nothing to send");
          stopTracks();
          reset();
          return;
        }

        if (blob.size > MAX_AUDIO_SIZE) {
          console.error("Audio recording exceeds max size");
          stopTracks();
          reset();
          return;
        }

        const url = URL.createObjectURL(blob);
        onFinish?.(blob, url);

        stopTracks();
        reset();
      };

      // Timeslice forces periodic chunks; Safari often yields an empty blob without it.
      recorder.start(RECORDING_TIMESLICE_MS);
      setRecording(true);
      startTimer();
    } catch (error) {
      console.error("Mic error:", error);
    }
  }, [clearTimer, clearStopTimeout, reset, startTimer, stopTracks, onFinish]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") return;

    clearStopTimeout();

    // Flush the final chunk so duration metadata is complete before stop
    if (recorder.state === "recording") {
      try {
        recorder.requestData();
      } catch (_) {
        // Some browsers throw if requestData is called at the wrong time
      }
    }

    // Safari can drop the final chunk if stop() runs in the same turn as requestData()
    if (isSafari()) {
      stopTimeoutRef.current = setTimeout(() => {
        stopTimeoutRef.current = null;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      }, SAFARI_STOP_DELAY_MS);
      return;
    }

    recorder.stop();
  }, [clearStopTimeout]);

  const cancelRecording = useCallback(() => {
    cancelledRef.current = true;
    clearStopTimeout();
    mediaRecorderRef.current?.stop();
  }, [clearStopTimeout]);

  useEffect(() => {
    return () => {
      clearTimer();
      clearStopTimeout();
      stopTracks();
      mediaRecorderRef.current?.stop();
    };
  }, [clearTimer, clearStopTimeout, stopTracks]);

  const formattedTime = useMemo(() => {
    const minutes = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return { minutes, secs };
  }, [seconds]);

  return useMemo(() => ({
    recording,
    seconds,
    minutes: formattedTime.minutes,
    secs: formattedTime.secs,
    startRecording,
    stopRecording,
    cancelRecording
  }), [
    recording,
    seconds,
    formattedTime,
    startRecording,
    stopRecording,
    cancelRecording
  ]);
};
